import {
  collapseAllIcon,
  expandAllIcon,
  fitAllIcon,
  layoutModeIcon,
} from "../icons.js";
import { renderEmptyState } from "./empty.js";
import { renderFocusCrumbs } from "./crumbs.js";
import { escapeAttr } from "../utils/html.js";
import { t } from "../../../../shared/i18n/index.js";

/**
 * Mindmap / Bubble tab.
 *
 * These were previously buried inside the "More" tab as a "Preview" toggle,
 * which put two views of the current document somewhere that also held account
 * settings and the log-out button. They are views, so they belong in the view
 * nav alongside Outline / Notes / Board / Timeline.
 *
 * The desktop view is mounted into the host div by desktop-preview-mount.js,
 * which keys off `data-mobile-desktop-view-host` matching the active tab.
 *
 * @param {"mindmap"|"bubble"} which
 */
export function renderMapView(appState, { callbacks, executionRevision = 0 }, which) {
  if (!appState.data) {
    return `
      <section class="mobile-view mobile-view-padded">
        ${renderEmptyState(t("mobile.empty.noDocument"), t("mobile.map.emptyText"))}
      </section>
    `;
  }

  const readOnly = callbacks.Store.isReadOnly?.();
  const unfold = escapeAttr(t("mobile.fold.unfoldOne"));
  const fold = escapeAttr(t("mobile.fold.foldFirst"));
  // No view title here — the bottom nav already says which view this is.
  // The freed space shows the focus breadcrumbs instead.
  return `
    <section class="mobile-view mobile-map-view">
      <div class="mobile-visual-frame mobile-desktop-view-frame mobile-map-frame">
        <div class="mobile-visual-toolbar">
          <div class="mobile-focus-strip in-toolbar">
            ${renderFocusCrumbs(appState, callbacks, { bare: true })}
          </div>
          <div class="mobile-visual-controls">
            ${readOnly ? "" : `
              <button class="mobile-icon-btn compact"
                      data-mobile-action="expand-all-nodes"
                      aria-label="${unfold}"
                      title="${unfold}">${expandAllIcon()}</button>
              <button class="mobile-icon-btn compact"
                      data-mobile-action="collapse-all-nodes"
                      aria-label="${fold}"
                      title="${fold}">${collapseAllIcon()}</button>
              <span class="mobile-toolbar-sep" aria-hidden="true"></span>
            `}
            ${which === "mindmap" ? `
              <button class="mobile-icon-btn compact"
                      data-mobile-action="cycle-mm-layout"
                      aria-label="${escapeAttr(t("mobile.map.cycleLayoutAria"))}"
                      title="${escapeAttr(t("mobile.map.cycleLayoutTitle"))}">${layoutModeIcon()}</button>
            ` : ""}
            <button class="mobile-icon-btn compact"
                    data-mobile-action="fit-desktop-view"
                    data-view="${which}"
                    aria-label="${escapeAttr(t("mobile.map.fitAria"))}"
                    title="${escapeAttr(t("mobile.map.fitTitle"))}">${fitAllIcon()}</button>
          </div>
        </div>
        <div class="mobile-desktop-view-host" data-mobile-desktop-view-host="${which}"
             data-horizon="${escapeAttr(JSON.stringify(appState.horizon))}"
             data-execution-revision="${executionRevision}"></div>
      </div>
    </section>
  `;
}
